import React, { useState, ReactNode } from 'react';
import { v4 as uuid } from 'uuid';

import { createContext, useContext, useContextSelector } from 'use-context-selector';
import { ScheduleContext } from './ScheduleContext';
import { ProductItemOriginal, ProductItemProps, ProductOriginal, Rule } from '../models/Product';
import { ScheduleItemProps } from '../models/Schedule';
import { ServiceContext } from './ServiceContext';

export type CartContextProps = {
  productList: ProductItemProps[];
  productListOriginal: ProductItemOriginal[];
  addProductList: (product: ProductItemProps, schedule?: ScheduleItemProps) => ScheduleItemProps | undefined;
  removeProductList: (product_id: string) => void;
  clearCart: () => void;
  changeProduct: (product: ProductItemProps) => void;
  findProduct: (productId: string) => ProductItemProps | undefined;
  findProductOriginal: (productId: string) => ProductItemOriginal | undefined;
  addProductsToCart: (product: ProductItemProps[]) => void;
};

type CartProviderProps = {
  children: ReactNode;
};

export const CartContext = createContext({} as CartContextProps);

export function CartProvider({ children }: CartProviderProps) {
  const [productList, setProductList] = useState<ProductItemProps[]>([]);
  const [productListOriginal, setProductListOriginal] = useState<ProductItemOriginal[]>([]);
  const { removeSchedule } = useContextSelector(ScheduleContext, state => state);
  const { selectedService } = useContextSelector(ServiceContext, state => state);

  const addProductList = (product: ProductItemProps, schedule?: ScheduleItemProps) => {
    product.idLocal = uuid();
    setProductList([...productList, product]);
    const originalProduct = ProductOriginal(product);
    setProductListOriginal([...productListOriginal, originalProduct]);

    if (schedule) {
      schedule.productIdLocal = product.idLocal;
      schedule.productId = product.id;

      schedule.price = product.price;
      schedule.discount = product.discount;
      // unitId?: string;
      // itemId?: string;
      // careType?: string;
      // payment?: Payment;
      schedule.productName = product.name;
      schedule.name = product.name;
      return schedule;
    }
  };

  const getIdLocalProduct = (productId: string) => {
    return productList.find(product => product.id === productId)?.idLocal;
  };

  function changeCatalogProductList({
    productFind,
    productToAdd
  }: {
    productFind: ProductItemProps;
    productToAdd: ProductItemProps;
  }) {
    const previousProduct = productListOriginal.find(item => item.id === productFind.id);

    const nexProduct = selectedService?.products.find(serviceProduct => serviceProduct.id === productToAdd.id);

    if (previousProduct && nexProduct) {
      const newCatalogProducts = productListOriginal.map(item => {
        if (item.id === previousProduct?.id) return nexProduct;
        return item;
      });
      setProductListOriginal(newCatalogProducts);
    }
  }

  const changeProduct = (product: ProductItemProps) => {
    const productIdLocal = product.idLocal || getIdLocalProduct(product.id);
    let hasFoundProduct = false;
    const newProductList = productList.map(item => {
      if (product.idLocal === productIdLocal) {
        hasFoundProduct = true;
        changeCatalogProductList({ productFind: item, productToAdd: product });
        return product;
      }
      return item;
    });
    if (!hasFoundProduct) newProductList.push(product);
    setProductList(newProductList);
  };

  const removeProductList = (product_id: string) => {
    const products = productList.filter(product => {
      if (product.idLocal === product_id) removeSchedule(product_id);
      if (product.idLocal !== product_id) return product;
    });
    setProductList(products);
  };

  const findProduct = (productId: string) => {
    return productList.find(product => product.id === productId);
  };

  const findProductOriginal = (productId: string) => {
    return productListOriginal.find(product => product.id === productId);
  };

  const clearCart = () => {
    setProductList([]);
    setProductListOriginal([]);
  };

  return (
    <CartContext.Provider
      value={{
        productList,
        productListOriginal,
        addProductList,
        removeProductList,
        clearCart,
        changeProduct,
        findProduct,
        findProductOriginal,
        addProductsToCart: setProductList
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCartContext() {
  return useContext(CartContext);
}
